import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { Spinner, Alert, Form, Button } from 'react-bootstrap';
import './EmployeeHierarchy.css';

function buildMaps(nodes) {
  const byId = new Map();
  const childrenByManager = new Map();

  for (const n of nodes) {
    const id = Number(n.emp_id);
    if (!Number.isFinite(id)) continue;
    byId.set(id, n);
  }

  for (const n of nodes) {
    const id = Number(n.emp_id);
    if (!Number.isFinite(id)) continue;
    const m = n.manager_id == null ? null : Number(n.manager_id);
    const managerId = Number.isFinite(m) ? m : null;
    if (!childrenByManager.has(managerId)) childrenByManager.set(managerId, []);
    childrenByManager.get(managerId).push(id);
  }

  for (const [k, arr] of childrenByManager.entries()) {
    arr.sort((a, b) => {
      const an = (byId.get(a)?.p_name || byId.get(a)?.emp_mail || '').toString().toLowerCase();
      const bn = (byId.get(b)?.p_name || byId.get(b)?.emp_mail || '').toString().toLowerCase();
      return an.localeCompare(bn);
    });
    childrenByManager.set(k, arr);
  }

  return { byId, childrenByManager };
}

function computeRoots(nodes, childrenByManager) {
  const ids = new Set(nodes.map((n) => Number(n.emp_id)).filter(Number.isFinite));
  // roots are those with manager_id null/0/unknown (not in the set)
  const roots = [];
  for (const n of nodes) {
    const id = Number(n.emp_id);
    if (!Number.isFinite(id)) continue;
    const m = n.manager_id == null ? null : Number(n.manager_id);
    const managerId = Number.isFinite(m) ? m : null;
    if (managerId == null || managerId === 0 || !ids.has(managerId)) roots.push(id);
  }
  // If no obvious roots (cyclic data), fall back to top-level manager null bucket
  if (roots.length === 0 && childrenByManager.has(null)) return childrenByManager.get(null);
  return Array.from(new Set(roots));
}

function getDisplayName(n) {
  return n?.p_name || n?.p_mail || n?.emp_mail || `Emp ${n?.emp_id ?? ''}`;
}

function NodeRow({ node, depth, hasChildren, expanded, onToggle, matched, activeReportsCount }) {
  const name = getDisplayName(node);
  return (
    <div className={`orgNodeRow ${matched ? 'matched' : ''}`} style={{ paddingLeft: depth * 18 }}>
      <div className="orgNodeMain">
        {hasChildren ? (
          <Button
            size="sm"
            variant="light"
            className="orgToggle"
            onClick={onToggle}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '−' : '+'}
          </Button>
        ) : (
          <span className="orgTogglePlaceholder" />
        )}

        <div className="orgNodeText">
          <div className="orgNodeName">
            <span className="orgNodeNameText">{name}</span>
            {activeReportsCount > 0 ? (
              <span className="orgCount" title="Active users under this person">
                {activeReportsCount}
              </span>
            ) : null}
          </div>
          <div className="orgNodeMeta">
            <span className="orgBadge">{node.emp_role}</span>
            <span className={`orgStatus ${node.is_active ? 'active' : 'inactive'}`}>
              {node.is_active ? 'Active' : 'Inactive'}
            </span>
            {node.emp_designation ? <span className="orgDim">{node.emp_designation}</span> : null}
            {node.emp_mail ? <span className="orgDim">{node.emp_mail}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EmployeeHierarchy() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nodes, setNodes] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // active | inactive | all
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    axiosInstance
      .get('/admin/org-tree')
      .then((res) => {
        if (!mounted) return;
        setNodes(Array.isArray(res.data) ? res.data : []);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e?.response?.data?.error || 'Failed to load hierarchy');
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filteredNodes = useMemo(() => {
    if (statusFilter === 'all') return nodes;
    const wantActive = statusFilter === 'active';
    return nodes.filter((n) => Boolean(n.is_active) === wantActive);
  }, [nodes, statusFilter]);

  // Count active descendants (all levels) for each emp_id from the full dataset.
  const activeReportsCountById = useMemo(() => {
    if (!nodes.length) return new Map();

    const { byId, childrenByManager } = buildMaps(nodes);
    const memo = new Map();

    const dfs = (id, visiting) => {
      if (memo.has(id)) return memo.get(id);
      if (visiting.has(id)) return 0; // cycle guard

      visiting.add(id);
      const kids = childrenByManager.get(id) || [];
      let total = 0;
      for (const k of kids) {
        const kn = byId.get(k);
        if (kn?.is_active) total += 1;
        total += dfs(k, visiting);
      }
      visiting.delete(id);

      memo.set(id, total);
      return total;
    };

    for (const id of byId.keys()) {
      dfs(id, new Set());
    }
    return memo;
  }, [nodes]);

  const { byId, childrenByManager, roots } = useMemo(() => {
    const maps = buildMaps(filteredNodes);
    const roots = computeRoots(filteredNodes, maps.childrenByManager);
    return { ...maps, roots };
  }, [filteredNodes]);

  const normalizedSearch = search.trim().toLowerCase();

  const matchSet = useMemo(() => {
    if (!normalizedSearch) return new Set();
    const s = new Set();
    for (const n of filteredNodes) {
      const hay = `${getDisplayName(n)} ${n.emp_mail || ''} ${n.emp_role || ''} ${n.emp_designation || ''}`.toLowerCase();
      if (hay.includes(normalizedSearch)) s.add(Number(n.emp_id));
    }
    return s;
  }, [filteredNodes, normalizedSearch]);

  const ensurePathExpanded = (targetId) => {
    const next = new Set(expanded);
    let curr = byId.get(targetId);
    // expand ancestors
    for (let i = 0; i < 50 && curr; i++) {
      const m = curr.manager_id == null ? null : Number(curr.manager_id);
      if (!Number.isFinite(m) || m === 0) break;
      next.add(m);
      curr = byId.get(m);
    }
    setExpanded(next);
  };

  useEffect(() => {
    if (!normalizedSearch) return;
    // Expand paths for first few matches for usability
    const ids = Array.from(matchSet).slice(0, 20);
    if (ids.length) {
      ids.forEach(ensurePathExpanded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedSearch]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderSubtree = (id, depth, acc) => {
    const node = byId.get(id);
    if (!node) return;

    const childIds = childrenByManager.get(id) || [];
    const hasChildren = childIds.length > 0;
    const isExpanded = expanded.has(id);
    const matched = matchSet.has(id);

    // If searching, hide nodes that are neither matched nor on a matched path (simple rule: show matched + ancestors + descendants under expanded nodes)
    if (normalizedSearch) {
      // show if matched OR has a matched descendant
      const hasMatchedDescendant = (function checkDescendants(x, limit = 5000) {
        const stack = [x];
        let seen = 0;
        while (stack.length && seen++ < limit) {
          const cur = stack.pop();
          const kids = childrenByManager.get(cur) || [];
          for (const k of kids) {
            if (matchSet.has(k)) return true;
            stack.push(k);
          }
        }
        return false;
      })(id);
      if (!matched && !hasMatchedDescendant) return;
    }

    acc.push(
      <NodeRow
        key={id}
        node={node}
        depth={depth}
        hasChildren={hasChildren}
        expanded={isExpanded}
        onToggle={() => toggle(id)}
        matched={matched}
        activeReportsCount={activeReportsCountById.get(id) || 0}
      />
    );

    if (hasChildren && (isExpanded || normalizedSearch)) {
      for (const childId of childIds) {
        renderSubtree(childId, depth + 1, acc);
      }
    }
  };

  const rows = useMemo(() => {
    const acc = [];
    for (const r of roots) renderSubtree(r, 0, acc);
    return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots, byId, childrenByManager, expanded, normalizedSearch, matchSet]);

  if (loading) {
    return (
      <div className="employeeHierarchyPage">
        <h2 className="employee-table-title">Employee Hierarchy</h2>
        <div className="hierLoading">
          <Spinner animation="border" role="status" />
        </div>
      </div>
    );
  }

  return (
    <div className="employeeHierarchyPage">
      <h2 className="employee-table-title">Employee Hierarchy</h2>

      <div className="manageEmployeesFilters hierarchyFilters">
        <div className="filterGroup">
          <label className="filterLabel">Status</label>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filterSelect"
            aria-label="Status filter"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </Form.Select>
        </div>

        <div className="filterGroup searchGroup">
          <label className="filterLabel">Search</label>
          <Form.Control
            value={search}
            placeholder="Search name / email / role"
            onChange={(e) => setSearch(e.target.value)}
            className="filterSearch"
          />
        </div>

        <div className="filterGroup">
          <label className="filterLabel">Tree</label>
          <div className="hierBtnRow">
            <Button
              variant="light"
              className="hierBtn"
              onClick={() => setExpanded(new Set(roots))}
              title="Expand top-level"
            >
              Expand
            </Button>
            <Button
              variant="light"
              className="hierBtn"
              onClick={() => setExpanded(new Set())}
              title="Collapse all"
            >
              Collapse
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <Alert variant="danger" className="hierAlert">
          {error}
        </Alert>
      ) : null}

      <div className="hierTreeContainer">
        {rows.length ? rows : <div className="hierEmpty">No employees found</div>}
      </div>
    </div>
  );
}

